import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "../lib/router.tsx";

export interface FolderPage {
  href: string;
  /** The small label over the name: the IRS form, or the step number. */
  code: string;
  name: string;
  line: string;
  current: boolean;
}

export interface FolderSection {
  name: string;
  /** The section's color: the stripe on its tab and the labels in its folder. */
  tone: "calc" | "guide" | "report";
  /** What the tab says under nothing open; replaced by the current page's name while you're inside the section. */
  meta: string;
  pages: FolderPage[];
}

/**
 * The top bar and its three tabs. Each tab opens a folder under the bar that pushes the page down rather than
 * covering it: the front cover tips down and the pages inside stand up side by side. `children` are the bar's other
 * items (the name, the theme button), placed before the tabs so keyboard focus goes from the tabs straight into an
 * open folder. Every folder stays in the page (hidden when closed) so its links are always there for crawlers and
 * the browser's find. Escape, a click outside, or going to a page closes it.
 */
export function FolderNav({ sections, path, children }: { sections: FolderSection[]; path: string; children?: ReactNode }) {
  const [open, setOpen] = useState<number | null>(null);
  // Going straight from one open folder to another swaps it in place instead of closing and growing again.
  const [swapped, setSwapped] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();

  useEffect(() => setOpen(null), [path]);
  useEffect(() => {
    if (open === null) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      tabRefs.current[open]?.focus();
      setOpen(null);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="folder-bar" ref={rootRef}>
      <div className="shell top-inner">
        {children}
        <nav className="folder-nav" aria-label="Site">
          <div className="folder-tabs">
            {sections.map((s, i) => {
              const here = s.pages.find((p) => p.current);
              const inside = here !== undefined || s.pages.some((p) => p.href.split("#")[0] === path);
              return (
                <button
                  key={s.name}
                  type="button"
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  className={`folder-tab folder-tab--${s.tone}` + (inside ? " is-here" : "")}
                  aria-expanded={open === i}
                  aria-controls={`${id}-${i}`}
                  onClick={() => {
                    setSwapped(open !== null && open !== i);
                    setOpen(open === i ? null : i);
                  }}
                >
                  <span className="folder-tab-meta">{here ? here.name : s.meta}</span>
                  <span className="folder-tab-name">{s.name}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
      {sections.map((s, i) => (
        <div key={s.name} id={`${id}-${i}`} className={`folder folder--${s.tone}` + (swapped ? " is-swap" : "")} hidden={open !== i}>
          <div className="shell">
            <div className="folder-scroll">
              <div className="folder-body">
                <ul className="folder-pages">
                  {s.pages.map((p, n) => (
                    <li key={p.href} style={{ "--i": n } as CSSProperties}>
                      <Link href={p.href} className="folder-page" aria-current={p.current ? "page" : undefined} onClick={() => setOpen(null)}>
                        <span className="folder-page-top">
                          <span className="folder-page-code">{p.code}</span>
                          {p.current && <span className="folder-here">You're here</span>}
                        </span>
                        <span className="folder-page-name">{p.name}</span>
                        <span className="folder-page-line">{p.line}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="folder-front" aria-hidden="true">
                  <span className="folder-label">{s.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
